import { useState } from "preact/hooks";
import { message } from "./errors.ts";

/**
 * The one shape every editing screen shares: run a bit of async work, disable
 * the controls while it is in flight, and show its failure in plain words
 * rather than let it throw. Each screen passes its own body; this holds the
 * `busy` and `error` around it so every screen's try/catch/finally is one copy.
 */
export interface Action {
  busy: boolean;
  error: string | null;
  setError: (value: string | null) => void;
  /**
   * Runs `body`, returning whether it finished without throwing so a caller can
   * clear a form only on success. An `onError` runs after the message is set —
   * the allowlist screen reloads its list there, because a refusal is often
   * about what changed underneath it.
   */
  run: (
    body: () => Promise<void>,
    onError?: () => void | Promise<void>,
  ) => Promise<boolean>;
}

export function useAction(): Action {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(
    body: () => Promise<void>,
    onError?: () => void | Promise<void>,
  ): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await body();
      return true;
    } catch (reason: unknown) {
      setError(message(reason));
      await onError?.();
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, setError, run };
}
